import React from 'react'
import CodeSheetStatusBanner from './CodeSheetStatusBanner'
import type { AppendixConsistencyResult } from '../../types/codeIndex'
import { describeAppendix, formatAppendixDisagreementLine } from '../../utils/appendixConsistency'

type UnselectableValuesBannerProps = {
  count: number
  // Something a reported finding depends on changed since the last save.
  needsRecheck: boolean
  // Valid-combination appendices that disagree with the rules; one line each.
  appendixResults?: AppendixConsistencyResult[]
  onOpenAppendix?: (appendixId: string) => void
}

export function formatUnselectableValuesCount(count: number): string {
  return `${count} value${count === 1 ? '' : 's'} can no longer be selected.`
}

export const UNSELECTABLE_VALUES_FIX_INSTRUCTION = 'Fix the rules, mark intentionally unavailable values as NA, or remove each value before submitting.'
export const UNSELECTABLE_VALUES_RECHECK_INSTRUCTION = 'Save the codesheet to recheck.'
export const APPENDIX_DISAGREEMENTS_FIX_INSTRUCTION = 'Change the rules or the table before submitting.'
export const CODESHEET_CHECKS_FIX_INSTRUCTION = 'Resolve each before submitting.'

/**
 * Shown above the codesheet after a save whose checks found something outstanding: values no
 * configuration can include, and appendices whose valid combinations the rules disagree with. Each
 * check contributes its own line. It cannot be dismissed; it goes away only when the next save
 * finds nothing, and looks the same whether or not editing has started.
 */
export default function UnselectableValuesBanner({
  count,
  needsRecheck,
  appendixResults = [],
  onOpenAppendix,
}: UnselectableValuesBannerProps) {
  const disagreeing = appendixResults.filter(result => result.findings.length > 0)
  const instruction = needsRecheck
    ? UNSELECTABLE_VALUES_RECHECK_INSTRUCTION
    : disagreeing.length === 0
      ? UNSELECTABLE_VALUES_FIX_INSTRUCTION
      : count === 0
        ? APPENDIX_DISAGREEMENTS_FIX_INSTRUCTION
        : CODESHEET_CHECKS_FIX_INSTRUCTION

  return (
    <CodeSheetStatusBanner testId="codesheet-unselectable-values-banner" variant="error">
      <div className="space-y-0.5">
        {count > 0 ? (
          <div data-testid="codesheet-check-line-unselectable" className="font-semibold">{formatUnselectableValuesCount(count)}</div>
        ) : null}
        {disagreeing.map(result => (
          <div key={result.appendixId} data-testid="codesheet-check-line-appendix">
            <span className="font-semibold">{formatAppendixDisagreementLine(result)}</span>
            {onOpenAppendix ? (
              <>
                {' '}
                <button
                  type="button"
                  className="cursor-pointer font-medium underline underline-offset-2 hover:text-[#8C1D18]"
                  onClick={() => onOpenAppendix(result.appendixId)}
                >
                  Open {describeAppendix(result)}
                </button>
              </>
            ) : null}
          </div>
        ))}
        <div data-testid="codesheet-unselectable-values-instruction">{instruction}</div>
      </div>
    </CodeSheetStatusBanner>
  )
}
